import React, { useEffect, useState } from 'react';
import {
  AlertTriangle, Download, ExternalLink, GraduationCap, Loader2, ServerCog,
} from 'lucide-react';
import { COLORS } from '../../data/mockData';

// Served from public/training/, copied out of docs/training/ at build time by
// frontend/scripts/sync-training-docs.js.
const MANUAL_URL = '/training/Dokploy-Multisite-Training-Manual.pdf';
const TOC_URL = '/training/manual-toc.json';

const card = {
  backgroundColor: COLORS.surface,
  borderRadius: 16,
  padding: '22px 24px',
  border: `1px solid ${COLORS.border}`,
  boxShadow: '0 2px 8px rgba(0,0,0,.04)',
  marginBottom: 16,
};
const h2 = {
  margin: '0 0 6px',
  fontSize: 22,
  fontWeight: 800,
  color: COLORS.textPrimary,
  fontFamily: 'Outfit, sans-serif',
};
const p = { margin: '0 0 10px', fontSize: 14, lineHeight: 1.6, color: COLORS.textPrimary };
const muted = { ...p, color: COLORS.textSecondary };
const button = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 16px',
  borderRadius: 10,
  border: 'none',
  backgroundColor: COLORS.primary,
  color: '#FFF',
  fontSize: 14,
  fontWeight: 700,
  cursor: 'pointer',
  textDecoration: 'none',
};
const secondaryButton = {
  ...button,
  backgroundColor: COLORS.surfaceSecondary,
  color: COLORS.textPrimary,
  border: `1px solid ${COLORS.border}`,
};

// Modules are numbered in the manual; everything else (home, quick reference,
// checklist, appendix) is supporting material.
const isModule = (title) => /^Module\s/.test(title);

export default function TrainingTab() {
  const [toc, setToc] = useState(null);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    fetch(TOC_URL)
      .then((r) => {
        if (!r.ok) throw new Error(`${r.status}`);
        return r.json();
      })
      .then((data) => { if (!cancelled) setToc(data); })
      .catch(() => { if (!cancelled) setError('unavailable'); });
    return () => { cancelled = true; };
  }, []);

  if (error) {
    return (
      <div style={card}>
        <h2 style={h2}>Operator Training</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <AlertTriangle size={18} color="#C62828" style={{ marginTop: 2, flexShrink: 0 }} />
          <div>
            <p style={p}>
              The training manual isn’t bundled with this build. It is generated from
              the source documentation and copied in during the frontend build.
            </p>
            <p style={muted}>
              To include it, run <code>python3 docs/training/export-pdf.py</code> and
              redeploy. The modules also live in the repository under{' '}
              <code>docs/training/</code>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!toc) {
    return (
      <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 10 }}>
        <Loader2 size={18} className="animate-spin" color={COLORS.textSecondary} />
        <span style={muted}>Loading the training manual…</span>
      </div>
    );
  }

  const modules = toc.chapters.filter((c) => isModule(c.title));
  const extras = toc.chapters.filter((c) => !isModule(c.title));

  return (
    <div>
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <GraduationCap size={22} color={COLORS.primary} />
          <h2 style={{ ...h2, margin: 0 }}>Operator Training</h2>
        </div>
        <p style={p}>
          <strong>Hosting Multiple Sites with Dokploy on a Hostinger VPS</strong> — the
          step-by-step manual for adding another Docker Compose web stack to the VPS so
          it serves an additional site, with its own domain, HTTPS certificate,
          containers, and database.
        </p>
        <p style={muted}>
          {modules.length} modules · {toc.pages} pages · diagrams, screenshots, and a
          launch checklist. Jump to any chapter below, or download the manual to print
          or read offline.
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
          <a style={button} href={MANUAL_URL} download data-testid="training-download">
            <Download size={16} /> Download PDF manual
          </a>
          <a style={secondaryButton} href={MANUAL_URL} target="_blank" rel="noreferrer">
            <ExternalLink size={16} /> Open in a new tab
          </a>
        </div>
      </div>

      <div style={card}>
        <h2 style={{ ...h2, fontSize: 18 }}>Curriculum</h2>
        <p style={muted}>
          Work through the modules in order the first time. Selecting one opens it in
          the viewer below.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
          {[
            ['Modules', modules],
            ['Reference material', extras],
          ].map(([groupLabel, chapters]) => chapters.length > 0 && (
            <React.Fragment key={groupLabel}>
              <div style={{
                fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase',
                color: COLORS.textSecondary, margin: '10px 0 2px',
              }}>
                {groupLabel}
              </div>
              {chapters.map((chapter) => {
                const active = chapter.page === page;
                return (
                  <button
                    key={`${chapter.page}-${chapter.title}`}
                    onClick={() => setPage(chapter.page)}
                    data-testid="training-chapter"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      width: '100%',
                      textAlign: 'left',
                      padding: '10px 14px',
                      borderRadius: 10,
                      cursor: 'pointer',
                      fontSize: 14,
                      fontWeight: active ? 700 : 500,
                      fontFamily: 'inherit',
                      color: active ? COLORS.primary : COLORS.textPrimary,
                      backgroundColor: active ? COLORS.surfaceSecondary : 'transparent',
                      border: `1px solid ${active ? COLORS.primary : COLORS.border}`,
                    }}
                  >
                    <span>{chapter.title}</span>
                    <span style={{ fontSize: 12, color: COLORS.textSecondary, whiteSpace: 'nowrap' }}>
                      p. {chapter.page}
                    </span>
                  </button>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <ServerCog size={18} color={COLORS.primary} />
          <h2 style={{ ...h2, fontSize: 18, margin: 0 }}>Manual</h2>
        </div>
        {/* Keyed on the page so changing chapters reloads the viewer at that page. */}
        <iframe
          key={page}
          src={`${MANUAL_URL}#page=${page}`}
          title="Dokploy multi-site training manual"
          data-testid="training-viewer"
          style={{
            width: '100%',
            height: '80vh',
            minHeight: 520,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 10,
            backgroundColor: COLORS.surfaceSecondary,
          }}
        />
        <p style={{ ...muted, margin: '10px 0 0' }}>
          Viewer not showing the manual? Use “Open in a new tab” above — some browsers
          block embedded PDFs.
        </p>
      </div>
    </div>
  );
}
